import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Check, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
const ImportModal = ({ isOpen, onClose, onConfirm, exportData }) => {
  const { t } = useTranslation('settings');
  const [selectedData, setSelectedData] = useState({
    notes: false,
    calendars: false,
    breaks: false,
  });
  const [overwrite, setOverwrite] = useState(false);

  useEffect(() => {
    if (exportData?.exported) {
      setSelectedData({
        notes: exportData.exported.notes || false,
        calendars: exportData.exported.calendars || false,
        breaks: exportData.exported.breaks || false,
      });
    }
  }, [exportData]);

  if (!isOpen) return null;

  const toggleData = (key) => {
    setSelectedData((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleConfirm = () => {
    if (!selectedData.notes && !selectedData.calendars && !selectedData.breaks) {
      return;
    }
    onConfirm({ selectedData, overwrite });
  };

  const modalClassName = 'rounded-lg shadow-elevated max-w-md w-full mx-4 overflow-hidden border border-white/10 transition-all duration-200 bg-[var(--color-bg-elevated)]';

  const headerClassName = 'flex items-center justify-between p-6 border-b border-white/10';

  const iconWrapperClass = 'w-12 h-12 rounded-xl flex items-center justify-center bg-orange-500/20';

  const iconClass = 'w-6 h-6 text-orange-400';

  const checkboxClass = 'w-5 h-5 rounded-sm border transition-all cursor-pointer border-white/10 bg-[var(--color-bg-elevated)]/ text-primary focus:ring-2 focus:ring-primary';

  const cancelButtonClass = 'px-6 py-2.5 rounded-xl transition-all duration-200 font-medium bg-[var(--color-bg-elevated)]/ border border-white/10 text-[var(--color-text-secondary)] hover:text-textPrimary hover:border-borderDefault';

  const confirmButtonClass = 'px-6 py-2.5 rounded-xl transition-all duration-200 font-semibold bg-[var(--color-bg-elevated)] hover:bg-[var(--color-surface-hover)] text-[var(--color-text-primary)] border border-white/10';

  const isAnySelected = selectedData.notes || selectedData.calendars || selectedData.breaks;

  const availableData = exportData?.exported || {};
  const hasNotes = availableData.notes && exportData?.data?.notes?.length > 0;
  const hasCalendars = availableData.calendars && exportData?.data?.calendars?.length > 0;
  const hasBreaks = availableData.breaks && exportData?.data?.breaks?.length > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 backdrop-blur-sm z-[100000] flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className={modalClassName}
            >
              {/* Header */}
              <div className={headerClassName}>
                <div className="flex items-center space-x-3">
                  <div className={iconWrapperClass}>
                    <Upload className={iconClass} strokeWidth={2.5} />
                  </div>
                  <h3 className="text-xl font-bold text-[var(--color-text-primary)]">
                    {t('importModal.title')}
                  </h3>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1, backgroundColor: 'var(--color-surface-hover)' }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 rounded-lg transition-all duration-200 text-[var(--color-text-muted)] hover:text-textPrimary hover:bg-[var(--color-bg-elevated)]/"
                  aria-label={t('importModal.close')}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-base leading-relaxed mb-6 text-[var(--color-text-secondary)]">
                  {t('importModal.description')}
                </p>

                {/* Checkboxes */}
                <div className="space-y-4 mb-6">
                  {/* Notes */}
                  <motion.label
                    whileHover={hasNotes ? { scale: 1.02 } : {}}
                    className={`flex items-center space-x-3 cursor-pointer p-3 rounded-xl transition-all ${
                      !hasNotes ? 'opacity-50 cursor-not-allowed' : ''
                    } ${
                      hasNotes
                        ? 'hover:bg-[#2563eb]/10'
                        : hasNotes
                        ? 'hover:bg-[var(--color-bg-elevated)]/ border border-transparent hover:border-borderDefault'
                        : ''
                    }`}
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={selectedData.notes}
                        onChange={() => hasNotes && toggleData('notes')}
                        disabled={!hasNotes}
                        className={checkboxClass}
                      />
                      {selectedData.notes && (
                        <Check className="w-4 h-4 text-[#3d8ef0] absolute top-0.5 left-0.5 pointer-events-none" />
                      )}
                    </div>
                    <span className="text-base font-medium text-[var(--color-text-secondary)]">
                      {t('importModal.notes')} {hasNotes && `(${exportData?.data?.notes?.length || 0})`}
                    </span>
                  </motion.label>

                  {/* Calendars */}
                  <motion.label
                    whileHover={hasCalendars ? { scale: 1.02 } : {}}
                    className={`flex items-center space-x-3 cursor-pointer p-3 rounded-xl transition-all ${
                      !hasCalendars ? 'opacity-50 cursor-not-allowed' : ''
                    } ${hasCalendars ? 'hover:bg-[var(--color-bg-elevated)]/ border border-transparent hover:border-borderDefault' : ''}`}
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={selectedData.calendars}
                        onChange={() => hasCalendars && toggleData('calendars')}
                        disabled={!hasCalendars}
                        className={checkboxClass}
                      />
                      {selectedData.calendars && (
                        <Check className="w-4 h-4 text-[#3d8ef0] absolute top-0.5 left-0.5 pointer-events-none" />
                      )}
                    </div>
                    <span className="text-base font-medium text-[var(--color-text-secondary)]">
                      {t('importModal.calendars')} {hasCalendars && `(${exportData?.data?.calendars?.length || 0})`}
                    </span>
                  </motion.label>

                  {/* Breaks */}
                  <motion.label
                    whileHover={hasBreaks ? { scale: 1.02 } : {}}
                    className={`flex items-center space-x-3 cursor-pointer p-3 rounded-xl transition-all ${
                      !hasBreaks ? 'opacity-50 cursor-not-allowed' : ''
                    } ${hasBreaks ? 'hover:bg-[var(--color-bg-elevated)]/ border border-transparent hover:border-borderDefault' : ''}`}
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={selectedData.breaks}
                        onChange={() => hasBreaks && toggleData('breaks')}
                        disabled={!hasBreaks}
                        className={checkboxClass}
                      />
                      {selectedData.breaks && (
                        <Check className="w-4 h-4 text-[#3d8ef0] absolute top-0.5 left-0.5 pointer-events-none" />
                      )}
                    </div>
                    <span className="text-base font-medium text-[var(--color-text-secondary)]">
                      {t('importModal.breaks')} {hasBreaks && `(${exportData?.data?.breaks?.length || 0})`}
                    </span>
                  </motion.label>
                </div>

                {/* Overwrite Checkbox */}
                <div className="mb-6 p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                  <motion.label
                    whileHover={{ scale: 1.01 }}
                    className="flex items-start space-x-3 cursor-pointer"
                  >
                    <div className="relative mt-0.5">
                      <input
                        type="checkbox"
                        checked={overwrite}
                        onChange={(e) => setOverwrite(e.target.checked)}
                        className={checkboxClass}
                      />
                      {overwrite && (
                        <Check className="w-4 h-4 text-[#3d8ef0] absolute top-0.5 left-0.5 pointer-events-none" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-orange-400" />
                        <span className="text-sm font-semibold text-orange-300">
                          {t('importModal.overwriteLabel')}
                        </span>
                      </div>
                      <p className="text-xs text-orange-300/80 leading-relaxed">
                        {t('importModal.overwriteDescription')}
                      </p>
                    </div>
                  </motion.label>
                </div>

                {/* Warning */}
                {!isAnySelected && (
                  <div className="mb-6 p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                    <p className="text-sm text-orange-300 leading-relaxed">
                      {t('importModal.selectAtLeastOne')}
                    </p>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex items-center justify-end space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02, backgroundColor: 'rgba(148, 163, 184, 0.1)' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className={cancelButtonClass}
                  >
                    {t('importModal.cancel')}
                  </motion.button>
                  <motion.button
                    whileHover={
                      isAnySelected ? { scale: 1.02, boxShadow: '0 10px 25px -5px rgba(249, 115, 22, 0.4)' } : {}
                    }
                    whileTap={{ scale: 0.98 }}
                    onClick={handleConfirm}
                    disabled={!isAnySelected}
                    className={`${confirmButtonClass} ${!isAnySelected ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {t('importModal.import')}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ImportModal;
